/**
 * Whichever booking action drawer/dialog is currently open, plus the
 * must-dismiss alert a resend may leave behind (BON-630). Split out of
 * `LoadedBooking` purely to keep that component's function under the
 * `max-lines-per-function` cap — same reasoning as `ReservationActionDialogs`,
 * whose ALERT-RENDERED-ALONGSIDE-NOT-INSTEAD-OF rule this mirrors: a resend
 * that failed to send may still have committed the email correction, so
 * closing `ResendConfirmationDialog` must not also lose the news.
 */
import type { BookingDetail } from '../../api/bookingsV2';
import { ErrorDialog } from '../../shared/ErrorDialog';

import { CancelBookingDialog } from './CancelBookingDialog';
import { RebookBookingDrawer } from './RebookBookingDrawer';
import { RefundBookingDrawer } from './RefundBookingDrawer';
import { ResendConfirmationDialog } from './ResendConfirmationDialog';
import type { BookingActionGating } from './useBookingActionGating';

export function BookingActionDialogs({
  booking,
  g,
}: {
  booking: BookingDetail;
  g: BookingActionGating;
}) {
  return (
    <>
      {g.rebookOpen && (
        <RebookBookingDrawer
          booking={booking}
          onClose={g.closeRebook}
          onRebooked={g.onBookingActionCompleted}
        />
      )}

      {g.cancelOpen && (
        <CancelBookingDialog
          booking={booking}
          onClose={g.closeCancel}
          onCancelled={g.onBookingActionCompleted}
        />
      )}
      {g.refundOpen && (
        <RefundBookingDrawer
          booking={booking}
          onClose={g.closeRefund}
          onRefunded={g.onBookingActionCompleted}
        />
      )}

      {g.resendConfirmationOpen && (
        <ResendConfirmationDialog
          booking={booking}
          onClose={g.closeResendConfirmation}
          onDone={g.onResendConfirmationDone}
          onRefresh={g.onBookingActionCompleted}
        />
      )}
      {g.resendConfirmationAlert && (
        <ErrorDialog
          title={g.resendConfirmationAlert.title}
          message={g.resendConfirmationAlert.message}
          tone={g.resendConfirmationAlert.tone}
          onClose={g.dismissResendConfirmationAlert}
        />
      )}
    </>
  );
}
